import React from "react";

export default function PrintInvoice({ order }) {
  if (!order) return null;

  const shipping = order.shipping || {};
  const items = order.items || [];

  const formatDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);

    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1,
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toFixed(2);
  };

  const totalQty = items.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0,
  );

  const subTotal = items.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
    0,
  );

  const discount = Number(order.discount || 0);

  // Use order.amount if it exists,
  // otherwise calculate from subtotal - discount
  const grandTotal =
    order.amount !== undefined && order.amount !== null
      ? Number(order.amount)
      : subTotal - discount;

  const invoiceNumber = order.invoiceNo || order.invoiceNumber || order._id;

  // =====================================================
  // NUMBER TO WORDS
  // =====================================================

  const numberToWords = (num) => {
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];

    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const convertBelowThousand = (n) => {
      let result = "";

      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + " Hundred ";
        n %= 100;
      }

      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + " ";
        n %= 10;
      }

      if (n > 0) {
        result += ones[n] + " ";
      }

      return result.trim();
    };

    num = Math.floor(Number(num) || 0);

    if (num === 0) {
      return "Zero";
    }

    let result = "";

    if (num >= 10000000) {
      result += convertBelowThousand(Math.floor(num / 10000000)) + " Crore ";
      num %= 10000000;
    }

    if (num >= 100000) {
      result += convertBelowThousand(Math.floor(num / 100000)) + " Lakh ";
      num %= 100000;
    }

    if (num >= 1000) {
      result += convertBelowThousand(Math.floor(num / 1000)) + " Thousand ";
      num %= 1000;
    }

    if (num > 0) {
      result += convertBelowThousand(num);
    }

    return result.trim();
  };

  const amountInWords = `Rupees ${numberToWords(grandTotal)} Only`;

  return (
    <div
      id="bill-of-supply"
      className="invoice-print bg-white text-black font-sans"
      style={{
        width: "210mm",
        minHeight: "297mm",
        boxSizing: "border-box",
        padding: "10mm",
        background: "#ffffff",
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="text-center p-3 border-b-[1.5px] border-black">
        <h1 className="text-base font-extrabold tracking-wider mb-0.5">
          LEGACY DECOR
        </h1>

        <p className="text-[10px] leading-snug">
          Shop 6 Ratnakar Atelier, Jodhpur Gam Road
          <br />
          Ramdev Nagar, Satellite, AHMEDABAD - 380015
          <br />
          GSTIN : 24AGOPJ5466B1ZG
          <br />
          Tel. : (+91) 7935565758
        </p>
      </div>

      {/* =====================================================
          BILL TITLE
      ===================================================== */}

      <div className="relative text-center py-1 border-b border-black font-bold text-xs">
        <span>BILL OF SUPPLY</span>

        <span className="absolute right-3 top-1 text-[10px]">
          Original Copy
        </span>
      </div>

      {/* =====================================================
          PARTY + INVOICE
      ===================================================== */}

      <div className="grid grid-cols-2 border-b-[1.5px] border-black">
        <div className="p-2 border-r border-black">
          <p className="font-bold">Party Details :</p>

          <p className="font-bold text-xs uppercase mt-0.5">
            {shipping.name || "CUSTOMER"}
          </p>

          <p className="mt-1 leading-snug">
            {shipping.address || ""}
            <br />
            {shipping.city || "AHMEDABAD"}
            {shipping.state ? `, ${shipping.state}` : ""}
            {shipping.pincode ? ` - ${shipping.pincode}` : ""}
            <br />
            Phone: {shipping.phone || "-"}
          </p>
        </div>

        <div className="p-2">
          <table className="w-full text-[10.5px]">
            <tbody>
              <tr>
                <td className="w-24 py-0.5">Trans. Type</td>

                <td className="w-2">:</td>

                <td className="font-medium">Intra State</td>
              </tr>

              <tr>
                <td className="py-0.5">Invoice No.</td>

                <td>:</td>

                <td className="font-medium">{invoiceNumber}</td>
              </tr>

              <tr>
                <td className="py-0.5">Date</td>

                <td>:</td>

                <td className="font-medium">{formatDate(order.createdAt)}</td>
              </tr>

              <tr>
                <td className="py-0.5">Ref. No.</td>

                <td>:</td>

                <td className="font-medium">{order.paymentId || "-"}</td>
              </tr>

              <tr>
                <td className="py-0.5">Ref. Date</td>

                <td>:</td>

                <td className="font-medium">{formatDate(order.createdAt)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* =====================================================
          PRODUCTS
      ===================================================== */}

      <table className="w-full border-collapse text-[10px]">
        <thead>
          <tr className="border-b border-black text-center font-bold bg-gray-50">
            <th rowSpan="2" className="border-r border-black w-8 p-1">
              Sr.
              <br />
              No.
            </th>

            <th rowSpan="2" className="border-r border-black p-1 text-left">
              Description Of Goods
            </th>

            <th rowSpan="2" className="border-r border-black w-10 p-1">
              Qty.
            </th>

            <th
              rowSpan="2"
              className="border-r border-black w-16 p-1 text-right"
            >
              Price
            </th>

            <th colSpan="2" className="border-r border-black p-1">
              SGST
            </th>

            <th colSpan="2" className="border-r border-black p-1">
              CGST
            </th>

            <th rowSpan="2" className="w-20 p-1 text-right">
              Amount
            </th>
          </tr>

          <tr className="border-b border-black text-center font-bold bg-gray-50">
            <th className="border-r border-black w-6 p-1">%</th>

            <th className="border-r border-black w-10 p-1">Amt.</th>

            <th className="border-r border-black w-6 p-1">%</th>

            <th className="border-r border-black w-10 p-1">Amt.</th>
          </tr>
        </thead>

        <tbody>
          {items.map((item, index) => {
            const itemTotal =
              Number(item.price || 0) * Number(item.quantity || 0);

            return (
              <tr key={`${item.productId}-${index}`} className="align-top">
                <td className="border-r border-black text-center p-1">
                  {index + 1}
                </td>

                <td className="border-r border-black p-1 font-semibold">
                  {item.title || "-"}
                </td>

                <td className="border-r border-black text-center p-1">
                  {item.quantity || 0}
                </td>

                <td className="border-r border-black text-right p-1">
                  {formatAmount(item.price)}
                </td>

                <td className="border-r border-black text-center p-1">0.00</td>

                <td className="border-r border-black text-right p-1">0.00</td>

                <td className="border-r border-black text-center p-1">0.00</td>

                <td className="border-r border-black text-right p-1">0.00</td>

                <td className="text-right p-1">{formatAmount(itemTotal)}</td>
              </tr>
            );
          })}

          {/* SUBTOTAL */}

          <tr className="border-t border-black font-semibold">
            <td className="border-r border-black"></td>

            <td className="border-r border-black text-right p-1">
              Sub Total
              <br />
              Discount
            </td>

            <td className="border-r border-black text-center">{totalQty}</td>

            <td className="border-r border-black"></td>

            <td className="border-r border-black"></td>
            <td className="border-r border-black"></td>
            <td className="border-r border-black"></td>
            <td className="border-r border-black"></td>

            <td className="text-right p-1">
              {formatAmount(subTotal)}
              <br />-{formatAmount(discount)}
            </td>
          </tr>

          {/* GRAND TOTAL */}

          <tr className="border-t-[1.5px] border-b-[1.5px] border-black font-bold bg-gray-50">
            <td colSpan="2" className="border-r border-black text-center p-1">
              Grand Total
            </td>

            <td className="border-r border-black text-center p-1">
              {totalQty}
            </td>

            <td className="border-r border-black"></td>
            <td className="border-r border-black"></td>
            <td className="border-r border-black"></td>
            <td className="border-r border-black"></td>
            <td className="border-r border-black"></td>

            <td className="text-right p-1 text-xs font-bold">
              {formatAmount(grandTotal)}
            </td>
          </tr>

          {/* GST */}

          <tr className="border-b border-black font-semibold text-[10px]">
            <td colSpan="9" className="p-1">
              Sale @ 0.00% / 0.00% = {formatAmount(grandTotal)} SGST = 0.00 CGST
              = 0.00
            </td>
          </tr>
        </tbody>
      </table>

      {/* =====================================================
          AMOUNT IN WORDS
      ===================================================== */}

      <div className="p-2 border-b border-black font-bold text-[10.5px]">
        {amountInWords}
      </div>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      <div className="grid grid-cols-2 min-h-[95px]">
        <div className="p-2 border-r border-black flex flex-col justify-between">
          <div>
            <p className="font-bold underline">Terms & Conditions</p>

            <p className="mt-0.5 text-[10px]">
              We are into Composite Scheme GST
            </p>
          </div>

          <p className="text-center font-bold tracking-widest text-[10px] mb-1">
            ******* THANKS FOR VISIT **********
          </p>
        </div>

        <div className="p-2 flex flex-col justify-between text-right">
          <p className="font-bold">For LEGACY DECOR</p>

          <p className="font-bold text-[10px]">Authorised Signatory</p>
        </div>
      </div>
    </div>
  );
}
